'use client';

import React, { useEffect, useState } from 'react';
import { refreshActiveTable } from '@/store/reducers/table-slice';
import { hideModal } from '@/store/reducers/ui-slice';
import { AdminRoles, CreatePermissionDto, PaginatedResponse, Permission } from '@alien-platform/bo-sdk-v2';
import { MethodResponse } from '@alien-platform/cms-bo';
import { Button, Grid } from '@mui/material';
import { Stack } from '@mui/system';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';

function CreatePermissionModalContent(): React.JSX.Element {
  // const { user } = useSelector((state: RootState) => state.user);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [permissionsList, setPermissionsList] = useState<{ label: string; value: number }[]>([]);

  const fetchData = async (): Promise<void> => {
    try {
      setIsDataLoading(true);
      const res = await fetch('/api/users/permissions/v2/list', {
        method: 'POST',
      });
      const result: MethodResponse<PaginatedResponse<Permission>> = await res.json();
      if (res.ok && result.success && result?.data?.items) {
        setPermissionsList(result.data.items.map((item) => ({ label: item.name, value: item.id })));
      }
    } catch (__) {
      // empty
    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const [dto, setDto] = useState<CreatePermissionDto>({
    name: '',
    desc: '',
    requiredMenuItems: [],
    allowedRoles: [],
    tags: [],
  });

  const generalInputs: CustomConfigInputProps[] = [
    {
      label: 'Title',
      name: 'name',
      value: dto.name,
      type: InputType.INPUT,
      zod: z.string().min(1, 'Name is required'),
    },
    {
      label: 'Description',
      name: 'desc',
      value: dto.desc,
      type: InputType.INPUT,
      zod: z.string().min(1, 'Desc is required'),
    },

    {
      label: 'Required Menu Items',
      name: 'requiredMenuItems',
      value: dto.requiredMenuItems,
      type: InputType.MULTISELECT,
      menuItems: [],
    },

    {
      label: 'Allowed Roles',
      name: 'allowedRoles',
      value: dto.allowedRoles,
      type: InputType.MULTISELECT,
      menuItems: Object.values(AdminRoles).map((item) => ({ label: item.replace(/_/g, ' '), value: item })),
    },
    {
      label: 'Tags',
      name: 'tags',
      value: dto.tags,
      type: InputType.INPUT,
    },
  ];

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = (): void => {
    dispatch(hideModal());
  };

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await fetch(`/api/permissions`, {
        method: 'POST',
        body: JSON.stringify(dto),
      });
      const result: MethodResponse<Permission> = await response.json();
      if (response.ok && result.success) {
        toast.success('Permission created successfully!');
        dispatch(hideModal());
        dispatch(refreshActiveTable());
      } else {
        toast.error(result.messages?.[0] || 'Error: 500 | Unable to create permission item');
      }
    } catch (e) {
      toast.error('Error: 500 | Unable to create permission item');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Grid container spacing={4}>
        {generalInputs.map((item, index) => (
          <CustomConfigInputV2 key={index} defaultData={dto} setDefaultData={setDto} {...item} />
        ))}
      </Grid>
      <Stack direction="row" spacing={3} sx={{ mt: 3, justifyContent: 'initial' }}>
        <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
          Close
        </Button>

        <Button
          loading={isLoading}
          onClick={handleSubmit}
          color="success"
          sx={{ flex: '1 1 auto' }}
          variant="contained"
        >
          Create Permission
        </Button>
      </Stack>
    </Stack>
  );
}

export default CreatePermissionModalContent;
